import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Registratie, Woning } from '@/types/domain';
import { getAuthService, getRegistratieService, getWoningService } from '@/services/serviceFactory';
import { offlineQueue, startOfflineSync, type WachtrijItem } from '@/services/offlineQueue';
import { draftService } from '@/services/draftService';
import { zetHuidigeRol } from '@/services/rolContext';
import { nieuweRegistratie } from '@/services/registratieFactory';
import type { Gebruiker } from '@/services/types';

/**
 * Eén gedeelde store voor registraties, woningen, de actieve selectie en de
 * ingelogde coach. Bewust zonder externe state-library: de app heeft weinig
 * globale state en dit blijft goed leesbaar.
 */

export interface Melding {
  id: number;
  soort: 'info' | 'succes' | 'waarschuwing' | 'fout';
  tekst: string;
}

interface AppState {
  gebruiker: Gebruiker | null;
  /** Alleen-lezen meekijker (gemeente); mag niets wijzigen. */
  alleenLezen: boolean;
  authGecontroleerd: boolean;
  registraties: Registratie[];
  /** Snelle opzoeking per BAG-id voor de kaartkleuren. */
  registratiePerBagId: Map<string, Registratie>;
  woningen: Woning[];
  ladenRegistraties: boolean;
  ladenWoningen: boolean;
  fout: string | null;
  geselecteerdeWoning: Woning | null;
  actieveRegistratie: Registratie | null;
  meldingen: Melding[];
  wachtrijAantal: number;
  /** Wat er nog verstuurd moet worden; voor het uitklapbare overzicht. */
  wachtrij: WachtrijItem[];
  online: boolean;
}

interface AppActies {
  inloggen: (email: string, wachtwoord: string) => Promise<void>;
  uitloggen: () => Promise<void>;
  selecteerWoning: (woning: Woning) => void;
  sluitFormulier: () => void;
  opslaan: (registratie: Registratie) => Promise<void>;
  verwijderen: (registratie: Registratie) => Promise<void>;
  laadWoningen: () => Promise<void>;
  /** Handmatig een punt zetten op een plek zonder BAG-woning. */
  plaatsPunt: (latitude: number, longitude: number) => Promise<void>;
  herlaadRegistraties: () => Promise<void>;
  toonMelding: (soort: Melding['soort'], tekst: string) => void;
  sluitMelding: (id: number) => void;
  openRegistratie: (registratie: Registratie) => void;
}

type AppContextWaarde = AppState & AppActies;

const AppContext = createContext<AppContextWaarde | null>(null);

export function AppProvider({ children }: { children: ReactNode }): JSX.Element {
  const [gebruiker, setGebruiker] = useState<Gebruiker | null>(null);
  const [authGecontroleerd, setAuthGecontroleerd] = useState(false);
  const [registraties, setRegistraties] = useState<Registratie[]>([]);
  const [woningen, setWoningen] = useState<Woning[]>([]);
  const [ladenRegistraties, setLadenRegistraties] = useState(false);
  const [ladenWoningen, setLadenWoningen] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [geselecteerdeWoning, setGeselecteerdeWoning] = useState<Woning | null>(null);
  const [actieveRegistratie, setActieveRegistratie] = useState<Registratie | null>(null);
  const [meldingen, setMeldingen] = useState<Melding[]>([]);
  const [wachtrij, setWachtrij] = useState<WachtrijItem[]>([]);
  const wachtrijAantal = wachtrij.length;
  const [online, setOnline] = useState(navigator.onLine);

  const meldingTeller = useRef(0);

  const toonMelding = useCallback((soort: Melding['soort'], tekst: string) => {
    meldingTeller.current += 1;
    const melding: Melding = { id: meldingTeller.current, soort, tekst };
    setMeldingen((huidig) => [...huidig, melding]);
    // Foutmeldingen blijven langer staan zodat ze buiten leesbaar zijn.
    const duur = soort === 'fout' ? 8000 : 3500;
    setTimeout(() => {
      setMeldingen((huidig) => huidig.filter((m) => m.id !== melding.id));
    }, duur);
  }, []);

  const sluitMelding = useCallback((id: number) => {
    setMeldingen((huidig) => huidig.filter((m) => m.id !== id));
  }, []);

  /* ---------------------------------------------------------------- */
  /* Inloggen                                                          */
  /* ---------------------------------------------------------------- */

  useEffect(() => {
    let actief = true;

    getAuthService()
      .huidigeGebruiker()
      .then((huidige) => {
        if (actief) setGebruiker(huidige);
      })
      .catch(() => {
        if (actief) setGebruiker(null);
      })
      .finally(() => {
        if (actief) setAuthGecontroleerd(true);
      });

    const opzeggen = getAuthService().abonneer((nieuwe) => {
      if (actief) setGebruiker(nieuwe);
    });

    return () => {
      actief = false;
      opzeggen();
    };
  }, []);

  const inloggen = useCallback(
    async (email: string, wachtwoord: string) => {
      setGebruiker(await getAuthService().inloggen(email, wachtwoord));
    },
    []
  );

  const uitloggen = useCallback(async () => {
    await getAuthService().uitloggen();
    setGebruiker(null);
    setRegistraties([]);
    setGeselecteerdeWoning(null);
    setActieveRegistratie(null);
  }, []);

  // De services lezen de rol hiervandaan om de juiste bron te kiezen.
  useEffect(() => {
    zetHuidigeRol(gebruiker?.rol ?? null);
  }, [gebruiker]);

  /* ---------------------------------------------------------------- */
  /* Laden                                                             */
  /* ---------------------------------------------------------------- */

  const herlaadRegistraties = useCallback(async () => {
    setLadenRegistraties(true);
    setFout(null);
    try {
      setRegistraties(await getRegistratieService().haalAlle());
    } catch (error) {
      const bericht =
        error instanceof Error ? error.message : 'Registraties konden niet worden geladen.';
      setFout(bericht);
      toonMelding('fout', bericht);
    } finally {
      setLadenRegistraties(false);
    }
  }, [toonMelding]);

  const laadWoningen = useCallback(async () => {
    setLadenWoningen(true);
    try {
      setWoningen(await getWoningService().haalWoningen());
    } catch (error) {
      toonMelding(
        'fout',
        error instanceof Error ? error.message : 'Woningen konden niet worden geladen.'
      );
    } finally {
      setLadenWoningen(false);
    }
  }, [toonMelding]);

  // Registraties pas ophalen zodra er iemand is ingelogd; zonder sessie
  // geeft de database toch niets terug.
  useEffect(() => {
    if (!gebruiker) return;
    void herlaadRegistraties();
    void laadWoningen();
    draftService.opruimen();
  }, [gebruiker, herlaadRegistraties, laadWoningen]);

  /* ---------------------------------------------------------------- */
  /* Online-status en offline-wachtrij                                 */
  /* ---------------------------------------------------------------- */

  useEffect(() => {
    const naarOnline = () => setOnline(true);
    const naarOffline = () => setOnline(false);
    window.addEventListener('online', naarOnline);
    window.addEventListener('offline', naarOffline);
    return () => {
      window.removeEventListener('online', naarOnline);
      window.removeEventListener('offline', naarOffline);
    };
  }, []);

  useEffect(() => offlineQueue.abonneer(setWachtrij), []);

  useEffect(() => {
    if (!gebruiker) return;
    return startOfflineSync((gesynchroniseerd) => {
      setRegistraties((huidig) => {
        const kaart = new Map(huidig.map((r) => [r.id, r]));
        gesynchroniseerd.forEach((r) => kaart.set(r.id, r));
        return [...kaart.values()];
      });
      toonMelding('succes', `${gesynchroniseerd.length} registratie(s) alsnog opgeslagen.`);
    });
  }, [gebruiker, toonMelding]);

  /* ---------------------------------------------------------------- */
  /* Live meekijken bij collega's                                      */
  /* ---------------------------------------------------------------- */

  useEffect(() => {
    if (!gebruiker) return;

    const dienst = getRegistratieService();
    if (!dienst.abonneerOpWijzigingen) return;

    return dienst.abonneerOpWijzigingen(
      (binnengekomen) => {
        setRegistraties((huidig) => {
          const index = huidig.findIndex((r) => r.id === binnengekomen.id);
          if (index < 0) return [...huidig, binnengekomen];

          // Niet overschrijven wat hier nog niet verstuurd is: anders ziet
          // een coach zijn eigen half ingevulde werk verspringen.
          if (huidig[index].syncState !== 'synced') return huidig;

          const kopie = [...huidig];
          kopie[index] = binnengekomen;
          return kopie;
        });
      },
      (verwijderdId) => {
        setRegistraties((huidig) => huidig.filter((r) => r.id !== verwijderdId));
      }
    );
  }, [gebruiker]);

  /* ---------------------------------------------------------------- */
  /* Selectie                                                          */
  /* ---------------------------------------------------------------- */

  const registratiePerBagId = useMemo(() => {
    const kaart = new Map<string, Registratie>();
    for (const registratie of registraties) kaart.set(registratie.bagId, registratie);
    return kaart;
  }, [registraties]);

  const selecteerWoning = useCallback(
    (woning: Woning) => {
      setGeselecteerdeWoning(woning);

      const bestaand = registratiePerBagId.get(woning.bagId);
      const concept = draftService.haal(bestaand?.id ?? '');

      if (bestaand) {
        // Een nieuwer concept wint van de opgeslagen versie.
        setActieveRegistratie(
          concept && concept.opgeslagenOp > bestaand.gewijzigdOp ? concept.registratie : bestaand
        );
      } else {
        setActieveRegistratie(nieuweRegistratie(woning, gebruiker?.naam ?? ''));
      }
    },
    [gebruiker, registratiePerBagId]
  );

  /**
   * Handmatig punt zetten, voor een plek waar de BAG geen woning kent:
   * een nieuwbouwkavel, een woonboot, of een adres dat net buiten de
   * BAG-selectie valt. Het dichtstbijzijnde adres wordt erbij gezocht als
   * startpunt; de coach kan dat in het formulier corrigeren.
   */
  const plaatsPunt = useCallback(
    async (latitude: number, longitude: number) => {
      const gevonden = await getWoningService()
        .adresBijPunt(latitude, longitude)
        .catch(() => null);

      const woning: Woning = {
        // Geen BAG-id: hieraan herkent de app een handmatig punt.
        bagId: '',
        adres: gevonden?.adres ?? {
          straat: '',
          huisnummer: '',
          postcode: '',
          plaats: '',
        },
        // Bewust het aangetikte punt, niet dat van het gevonden adres.
        latitude,
        longitude,
      };

      setGeselecteerdeWoning(woning);
      setActieveRegistratie(nieuweRegistratie(woning, gebruiker?.naam ?? ''));
    },
    [gebruiker]
  );

  const openRegistratie = useCallback((registratie: Registratie) => {
    setGeselecteerdeWoning({
      bagId: registratie.bagId,
      adres: registratie.adres,
      latitude: registratie.latitude,
      longitude: registratie.longitude,
    });
    setActieveRegistratie(registratie);
  }, []);

  const sluitFormulier = useCallback(() => {
    setGeselecteerdeWoning(null);
    setActieveRegistratie(null);
  }, []);

  /* ---------------------------------------------------------------- */
  /* Muteren                                                           */
  /* ---------------------------------------------------------------- */

  const opslaan = useCallback(
    async (registratie: Registratie) => {
      const teBewaren: Registratie = { ...registratie, gewijzigdOp: new Date().toISOString() };

      // Optimistisch bijwerken: de coach ziet het resultaat direct.
      setRegistraties((huidig) => {
        const index = huidig.findIndex((r) => r.id === teBewaren.id);
        if (index < 0) return [...huidig, teBewaren];
        const kopie = [...huidig];
        kopie[index] = teBewaren;
        return kopie;
      });

      try {
        const opgeslagen = await getRegistratieService().opslaan(teBewaren);
        setRegistraties((huidig) => huidig.map((r) => (r.id === opgeslagen.id ? opgeslagen : r)));
        setActieveRegistratie((huidig) => (huidig?.id === opgeslagen.id ? opgeslagen : huidig));
        draftService.verwijder(opgeslagen.id);
        offlineQueue.verwijderen(opgeslagen.id);
        toonMelding('succes', 'Registratie opgeslagen.');
      } catch (error) {
        const bericht = error instanceof Error ? error.message : String(error);
        offlineQueue.toevoegen({ ...teBewaren, syncState: 'error', syncFout: bericht }, bericht);
        setRegistraties((huidig) =>
          huidig.map((r) =>
            r.id === teBewaren.id ? { ...r, syncState: 'error', syncFout: bericht } : r
          )
        );
        toonMelding(
          'fout',
          navigator.onLine
            ? `Opslaan mislukt: ${bericht} De registratie staat in de wachtrij.`
            : 'Geen verbinding. De registratie staat in de wachtrij en wordt later verstuurd.'
        );
      }
    },
    [toonMelding]
  );

  const verwijderen = useCallback(
    async (registratie: Registratie) => {
      try {
        await getRegistratieService().verwijderen(registratie);
        setRegistraties((huidig) => huidig.filter((r) => r.id !== registratie.id));
        draftService.verwijder(registratie.id);
        offlineQueue.verwijderen(registratie.id);
        sluitFormulier();
        toonMelding('succes', 'Registratie verwijderd.');
      } catch (error) {
        toonMelding('fout', error instanceof Error ? error.message : 'Verwijderen is niet gelukt.');
      }
    },
    [sluitFormulier, toonMelding]
  );

  const waarde: AppContextWaarde = useMemo(
    () => ({
      gebruiker,
      alleenLezen: gebruiker?.rol === 'gemeente',
      authGecontroleerd,
      registraties,
      registratiePerBagId,
      woningen,
      ladenRegistraties,
      ladenWoningen,
      fout,
      geselecteerdeWoning,
      actieveRegistratie,
      meldingen,
      wachtrijAantal,
      wachtrij,
      online,
      inloggen,
      uitloggen,
      selecteerWoning,
      sluitFormulier,
      opslaan,
      verwijderen,
      laadWoningen,
      plaatsPunt,
      herlaadRegistraties,
      toonMelding,
      sluitMelding,
      openRegistratie,
    }),
    [
      gebruiker,
      authGecontroleerd,
      registraties,
      registratiePerBagId,
      woningen,
      ladenRegistraties,
      ladenWoningen,
      fout,
      geselecteerdeWoning,
      actieveRegistratie,
      meldingen,
      wachtrijAantal,
      online,
      inloggen,
      uitloggen,
      selecteerWoning,
      sluitFormulier,
      opslaan,
      verwijderen,
      laadWoningen,
      plaatsPunt,
      herlaadRegistraties,
      toonMelding,
      sluitMelding,
      openRegistratie,
    ]
  );

  return <AppContext.Provider value={waarde}>{children}</AppContext.Provider>;
}

export function useApp(): AppContextWaarde {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp moet binnen een <AppProvider> worden gebruikt.');
  return context;
}
